import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface Module {
  id: number;
  name: string;
  addedBy: string;
  addedDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
  inActive: boolean;
  inActiveReason: string | null;
  inActiveBy: string | null;
  inActiveDate: string | null;
}

interface Permission {
  id: number;
  moduleId: number;
  canAccess: boolean;
  canEdit: boolean;
  canView: boolean;
  canAdd: boolean;
  canDelete: boolean;
  addedBy: string;
  addedDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
  inActive: boolean;
  inActiveReason: string | null;
  inActiveBy: string | null;
  inActiveDate: string | null;
}

interface PermissionsState {
  modules: Module[];
  permissions: Permission[];
  // Permissions V2 — flat list of granted permission keys, each optionally carrying a
  // scope suffix ("employees.view@team"). Delivered alongside `permissions` (legacy rights)
  // at login during the migration window.
  permissionKeys: string[];
  // Product modules switched on for the user's company (backend FeatureModuleCatalog
  // codes, e.g. "training", "fleet"). permissionKeys already exclude the others; this
  // gates screens and widgets that aren't driven by a key. null = not known yet
  // (session predates the field) and is treated as every module on.
  enabledModules: string[] | null;
}

// Helper function to save state to localStorage
const saveState = (state: PermissionsState) => {
  localStorage.setItem("permissionsState", JSON.stringify(state));
};

// Helper function to load state from localStorage
const loadState = (): PermissionsState | null => {
  try {
    const serializedState = localStorage.getItem("permissionsState");
    if (serializedState === null) {
      return null;
    }
    const parsed = JSON.parse(serializedState);
    // Back-compat: older cached state predates permissionKeys.
    if (!Array.isArray(parsed.permissionKeys)) {
      parsed.permissionKeys = [];
    }
    if (!Array.isArray(parsed.enabledModules)) {
      parsed.enabledModules = null;
    }
    return parsed;
  } catch (err) {
    return null;
  }
};

const initialState: PermissionsState = loadState() || {
  modules: [],
  permissions: [],
  permissionKeys: [],
  enabledModules: null,
};

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setModules: (state, action: PayloadAction<Module[]>) => {
      state.modules = action.payload;
      saveState(state);
    },
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
      saveState(state);
    },
    setPermissionKeys: (state, action: PayloadAction<string[]>) => {
      state.permissionKeys = Array.isArray(action.payload) ? action.payload : [];
      saveState(state);
    },
    // Ignores a missing value (older API) so it never wipes a known list.
    setEnabledModules: (state, action: PayloadAction<string[] | null | undefined>) => {
      if (!Array.isArray(action.payload)) return;
      state.enabledModules = action.payload;
      saveState(state);
    },
    clearPermissions: (state) => {
      state.modules = [];
      state.permissions = [];
      state.permissionKeys = [];
      state.enabledModules = null;
      localStorage.removeItem("permissionsState");
    },
  },
});

export const { setModules, setPermissions, setPermissionKeys, setEnabledModules, clearPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;
